import React from 'react'
import { NextSeo, BreadcrumbJsonLd } from 'next-seo'
import coverImages from '~/lib/cover-images'
import { LISTING_VIEWED } from '~/lib/tracking-events'
import Error from './_error'
import { getCategories, getLinkProps, track } from '../utils'
import { withTranslation } from '~/i18n'
import ListingPageContent from '~/components/ListingPageContent'
import LegacyListingPageContent from '~/components/LegacyListingPageContent'
import AdminMenu from '../components/AdminMenu/dynamic'
import Testimonials from '../components/Testimonials'
import SiteFooter from '../components/SiteFooter'
import FeedbackWidget from '../components/FeedbackWidget'
import CustomPageTracking from '../components/CustomPageTracking'
import { decodeGlobalId } from '~/utils/id'
import getViewer from '~/lib/get-viewer'
import { useViewer } from '~/components/ViewerContext'
import { GET_CART_AND_LISTING } from '~/graphql/queries/getCartAndListing'

const Listing = ({ t, id, hash, data, randomCover, adminUrl }) => {
  const { viewer } = useViewer()

  if (!data.listing) {
    return <Error statusCode={404} />
  }

  const { listing = {} } = data
  const { event = {}, eventType = {} } = listing

  // Create Breadcrumbs array based on existing event and eventTypes info
  const categoryList = getCategories(t)
  const eventCategory = event.category ? categoryList.find(el => el.value === event.category) : null
  const breadcrumbs = [
    ...(eventCategory
      ? [
          {
            position: 1,
            name: eventCategory?.name,
            item: getLinkProps('CATEGORY', { slug: eventCategory.slug }).as,
          },
        ]
      : []),
    ...(event?.location?.city
      ? [
          {
            position: 2,
            name: event.location.city.name,
            item: getLinkProps('CITY', {
              slug: event.location.city.slug,
              id: decodeGlobalId(event.location.city.id),
            }).as,
          },
        ]
      : []),
    ...(event?.location?.name
      ? [
          {
            position: 3,
            name: event.location.name,
            item: getLinkProps('LOCATION', {
              slug: event.location.slug,
              id: decodeGlobalId(event.location.id),
            }).as,
          },
        ]
      : []),
    ...(event.name
      ? [
          {
            position: 4,
            name: event.name,
            item: getLinkProps('EVENT_PAGE', {
              eventSlug: event.slug,
              eventId: decodeGlobalId(event.id),
            }).as,
          },
        ]
      : []),
    ...(eventType
      ? [
          {
            position: 5,
            name: eventType.title,
            item: getLinkProps('EVENT_TYPE_PAGE', {
              eventSlug: event.slug,
              eventId: decodeGlobalId(event.id),
              eventTypeSlug: eventType.slug,
              eventTypeId: decodeGlobalId(eventType.id),
            }).as,
          },
        ]
      : []),
    ...(listing.id
      ? [
          {
            position: 6,
            name: t('listing:purchaser.listing'),
            item: getLinkProps('LISTING_PAGE', {
              slug: event.slug,
              id: decodeGlobalId(listing.id),
              hash: listing.hash,
            }).as,
          },
        ]
      : []),
  ]

  return (
    <>
      <CustomPageTracking
        handle={LISTING_VIEWED}
        payload={{
          id: decodeGlobalId(listing.id),
          eventId: decodeGlobalId(event.id),
          eventName: event.name,
          ...(eventType
            ? {
                eventTypeId: decodeGlobalId(eventType.id),
                eventTypeName: eventType.title,
              }
            : {}),
          availableTickets: listing.numberOfTicketsStillForSale,
        }}
      />
      <NextSeo
        title={`${event.seoMetadata?.title || event.name} – ${t('common:site.shortTitle')} – ${t(
          'common:site.name'
        )}`}
        description={
          event.seoMetadata?.description
            ? event.seoMetadata.description
            : t('common:site.description')
        }
        openGraph={{
          title: event?.seoMetadata?.title || event.name,
          description: event.seoMetadata?.description
            ? event.seoMetadata.description
            : t('common:site.description'),
          site_name: t('common:site.name'),
        }}
        noindex={true}
        nofollow={true}
      />
      <BreadcrumbJsonLd itemListElements={breadcrumbs} />
      {Boolean(viewer?.isEmployee) ? (
        <ListingPageContent t={t} id={id} data={data} hash={hash} randomCover={randomCover} />
      ) : (
        <LegacyListingPageContent t={t} id={id} data={data} hash={hash} randomCover={randomCover} />
      )}
      <AdminMenu links={[{ text: 'Open listing', url: adminUrl }]} />
      <FeedbackWidget />
      <Testimonials />
      <SiteFooter />
    </>
  )
}

Listing.defaultProps = {
  data: {},
}

Listing.getInitialProps = async ({ req, query, apolloClient }) => {
  const viewer = await getViewer(apolloClient, req)
  const isLoggedIn = Boolean(viewer)
  const { id, hash: queryHash, slug } = query
  let data = {}
  // Sometimes the UTM querystring is attached wrongfully to the url.
  // The hash is always the first 10 chars in this case.
  const hash = queryHash.substring(0, 10)

  try {
    const res = await apolloClient.query({
      fetchPolicy: 'network-only',
      query: GET_CART_AND_LISTING,
      variables: { id, hash, isLoggedIn },
    })
    data = res.data
  } catch (error) {
    if (!error.message.includes('Could not find Listing')) {
      await track('LISTING_VIEWED_FAILED', {
        error,
        id,
        hash,
        slug,
      })
    }
  }

  const randomCover = coverImages.default[Math.floor(Math.random() * coverImages.default.length)]

  return {
    data,
    namespacesRequired: ['common', 'listing', 'event-eventtype'],
    eventSlug: slug,
    id,
    hash,
    adminUrl: `/ads/show/${id}`,
    randomCover,
  }
}

export default withTranslation(['common', 'listing', 'event-eventtype'])(Listing)
